import { Link } from "react-router-dom";
import { useState } from "react";
import { Lightbulb, ChevronDown, Sparkles, Crown } from "lucide-react";
import { StrongPick, coverageLabel } from "@/components/StrongPick";
import { LockedGuess } from "@/components/UnlockGuess";
import { formatResultValue, istNow, timeToMinutes, revealedValue, chartPath } from "@/utils/format";

function GuessingPanel({ guessing, testid, onUnlocked }) {
  const [open, setOpen] = useState(false);
  if (guessing.locked) {
    return (
      <div className="border-t border-dashed border-[color:var(--border-gold)] bg-[color:var(--bg-card)]/60">
        <LockedGuess guessing={guessing} testid={testid} onUnlocked={onUnlocked} />
      </div>
    );
  }
  const cov = coverageLabel(guessing);
  const rows = [
    [`${cov}Open-Close Ank`, guessing.ank],
    [`${cov}Patti`, guessing.patti],
    [`${cov}Jodi`, guessing.jodi],
    ["Half Sangam", guessing.half_sangam],
    ["Full Sangam", guessing.full_sangam],
  ].filter(([, v]) => v);
  if (!rows.length) return null;
  return (
    <div className="border-t border-dashed border-[color:var(--border-gold)] bg-[color:var(--bg-card)]/60">
      <button
        data-testid={`guessing-toggle-${testid}`}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-center gap-1.5 py-1.5 text-[11px] font-extrabold uppercase tracking-wide text-[color:var(--accent-crimson)] transition-colors hover:text-[color:var(--accent-gold-bright)]"
      >
        <Lightbulb className="h-3.5 w-3.5" /> Today's Guessing
        {guessing.ai && (
          <span data-testid={`guessing-ai-badge-${testid}`} className="inline-flex items-center gap-0.5 rounded-full bg-indigo-600 px-1.5 py-[1px] text-[8px] font-extrabold uppercase tracking-wide text-white">
            <Sparkles className="h-2.5 w-2.5" /> Smart Guess
          </span>
        )}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid={`guessing-panel-${testid}`} className="space-y-1.5 px-3 pb-3 pt-0.5">
          <StrongPick guessing={guessing} testid={testid} />
          {rows.map(([label, val]) => (
            <div key={label} className="flex flex-col items-center gap-0.5 text-center">
              <span className="text-[9px] font-bold uppercase tracking-wider text-[color:var(--text-muted)]">{label}</span>
              <span className="break-words font-mono text-xs sm:text-sm font-extrabold leading-relaxed tracking-wide text-[color:var(--text-primary)]">{val}</span>
            </div>
          ))}
          <p className="pt-1 text-center text-[8px] italic text-[color:var(--text-muted)]">
            For information only · public source guessing
          </p>
        </div>
      )}
    </div>
  );
}

export function isMarketActive(open_time, close_time) {
  const o = timeToMinutes(open_time);
  const c = timeToMinutes(close_time);
  if (o == null || c == null) return false;
  const now = istNow();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  if (c >= o) return nowMin >= o && nowMin <= c;
  return nowMin >= o || nowMin <= c; // overnight window
}

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const ORDER = [1, 2, 3, 4, 5, 6, 0];

function daysLabel(off = []) {
  const on = ORDER.filter((d) => !off.includes(d));
  if (on.length === 7) return "Daily";
  if (!on.length) return "";
  const start = ORDER.findIndex((d) => !off.includes(d) && off.includes(ORDER[(ORDER.indexOf(d) + 6) % 7]));
  const run = [...ORDER.slice(start), ...ORDER.slice(0, start)].slice(0, on.length);
  if (start >= 0 && run.every((d) => on.includes(d))) return on.length === 1 ? WD[run[0]] : `${WD[run[0]]}-${WD[run[run.length - 1]]}`;
  return on.map((d) => WD[d]).join(", ");
}

export function MarketRow({ result, index = 0, testid, own = false, onRefresh, guessing = null, streak = 0, passed = false, onGuessUnlocked, rate = null, still = false }) {
  const holiday = Array.isArray(result.off_days) && result.off_days.includes(istNow().getDay());
  const value = holiday ? "HOLIDAY" : revealedValue(result);
  const active = !holiday && isMarketActive(result.open_time, result.close_time);
  const timing =
    result.open_time && result.close_time
      ? `(${result.open_time} - ${result.close_time})`
      : result.open_time
      ? `(${result.open_time})`
      : "";
  const nameColor = "#5A0A14";
  const halfDone = !holiday && /^\d{3}-\d\*?-\*{3}$/.test(String(value).replace(/\s/g, ""));

  return (
    <div className={`ring-fx my-2 rounded-xl ${still ? "ring-still" : ""}`}>
    <div
      data-testid={testid || `market-row-${result.id}`}
      className={`relative flex items-stretch ${
        active ? "bg-[#FFF3B0]" : "matka-row"
      }`}
    >
      {/* Jodi (left) */}
      <Link
        to={chartPath(result.category_name, "jodi")}
        data-testid={`market-jodi-${result.id}`}
        className="flex shrink-0 items-center px-2"
      >
        <span data-testid={`btn-ring-jodi-${result.id}`} className="btn-ring transition-transform hover:scale-105">
          <span className="rounded-full bg-[color:var(--accent-crimson)] px-3.5 py-1.5 text-[13px] font-extrabold uppercase tracking-wide text-white">
            Jodi
          </span>
        </span>
      </Link>

      {/* Center */}
      <div className="flex flex-1 flex-col items-center justify-center py-2 text-center">
        <span className="mr-name flex items-center justify-center gap-1.5 font-display text-[13px] sm:text-sm font-extrabold uppercase tracking-wide" style={{ color: nameColor }}>
          {active && (
            <span data-testid={`live-badge-${result.category_id}`} className="inline-flex items-center gap-1 rounded-full bg-[#0B7A3B] px-1.5 py-0.5 text-[8px] font-extrabold uppercase not-italic tracking-wider text-white">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-75"></span>
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-200"></span>
              </span>
              Live
            </span>
          )}
          {passed && <Crown data-testid={`king-left-${result.category_id}`} className="h-4 w-4 fill-[#F5B800] text-[#B8860B]" />}
          <span translate="no" className="notranslate">{result.category_name}</span>
          {passed && <Crown data-testid={`king-right-${result.category_id}`} className="h-4 w-4 fill-[#F5B800] text-[#B8860B]" />}
          {guessing?.locked && guessing.price > 0 && (
            <span data-testid={`paid-badge-${result.category_id}`} className="paid-badge rounded-full px-1.5 py-0.5 text-[9px] font-black uppercase not-italic tracking-wider text-white">
              ₹ Paid
            </span>
          )}
          {["silver", "gold"].includes(result.listing_tier) && (
            <span data-testid={`tier-badge-${result.category_id}`} className={`premium-blink rounded-full px-1.5 py-0.5 text-[9px] font-black uppercase not-italic tracking-wider ${result.listing_tier === "gold" ? "bg-gradient-to-r from-amber-300 to-yellow-500 text-[#3b1d00]" : "bg-gradient-to-r from-slate-200 to-slate-400 text-[#1f2937]"}`}>
              {result.listing_tier === "gold" ? "★ Gold" : "Silver"}
            </span>
          )}
          {guessing?.unlocked && (
            <span data-testid={`unlocked-badge-${result.category_id}`} className="rounded-full bg-[#0B7A3B] px-1.5 py-0.5 text-[9px] font-black uppercase not-italic tracking-wider text-white shadow-[0_0_8px_rgba(16,185,129,0.8)]">
              ✓ Unlocked
            </span>
          )}
        </span>
        <span className={`mr-value font-mono text-xl sm:text-[22px] font-extrabold leading-tight tracking-wider ${holiday ? "text-[#2563C9]" : active ? "text-[#20050B]" : "text-[#5A0A14]"}`}>
          <span translate="no" className="notranslate">{value}</span>
        </span>
        {timing && (
          <span className="mr-time text-[10px] font-medium text-[#777]">
            {timing}
          </span>
        )}
        {halfDone && (
          <span data-testid={`loading-badge-${result.category_id}`} translate="no" className="notranslate loading-blink mt-0.5 bg-[#90EE90] px-1.5 font-mono text-[12px] font-bold text-[#E00000]">
            LOADING ..
          </span>
        )}
        {daysLabel(result.off_days || []) && (
          <span data-testid={`days-badge-${result.category_id}`} translate="no" className="notranslate mt-0.5 rounded bg-[#1E8A4C] px-2.5 py-[1px] text-[10px] font-semibold text-white">
            {daysLabel(result.off_days || [])}
          </span>
        )}
      </div>

      {/* Panel (right) */}
      <Link
        to={chartPath(result.category_name, "panel")}
        data-testid={`market-panel-${result.id}`}
        className="flex shrink-0 items-center px-2"
      >
        <span data-testid={`btn-ring-panel-${result.id}`} className="btn-ring transition-transform hover:scale-105">
          <span className="rounded-full bg-[color:var(--accent-crimson)] px-3.5 py-1.5 text-[13px] font-extrabold uppercase tracking-wide text-white">
            Panel
          </span>
        </span>
      </Link>
    </div>
    {guessing && <GuessingPanel guessing={guessing} testid={result.category_id} onUnlocked={onGuessUnlocked} />}
    {guessing && rate?.played >= 1 && (
      <p data-testid={`pass-rate-${result.category_id}`} className="flex flex-wrap items-center justify-center gap-1.5 border-t border-dashed border-[color:var(--border-gold)] bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-800">
        <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-white">{rate.passed}/{rate.week_days || 7} days PASS</span>
        <span>Jodi {rate.jodi} days</span>
      </p>
    )}
    </div>
  );
}
